/*

  Main.ts

  Main screen component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { Component, m } from "./mithril";
import { glyph, glyphGrayed, nbsp } from "./util";
import { mt, t, ts } from "./i18n";
import { HeaderLogo, Info, LeftLogo, ProModeIndicator, TogglerReady, TogglerReadyTimed, UserId } from "./components";
import { RecentList } from "./recentlist";
import { GameList } from "./gamelist";
import { ChallengeList } from "./challengelist";
import { UserList } from "./userlist";
import { StatsDisplay } from "./statsdisplay";
import { SearchButton } from "./searchbutton";
import { makeTabs, updateSelection } from "./tabs";
import { BestDisplay } from "./bestdisplay";

interface IAttributes {
  view: IView;
}

// Local components for MainTabs

const MainTabHeader: Component<IAttributes> = {
  // Tab navigation headers with game/challenge counts
  view: (vnode) => {
    const { view } = vnode.attrs;
    const { model } = view;
    const { numGames, numChallenges } = model;
    return [
      m(HeaderLogo, { hidden: true }),
      m("ul", [
        m("li",
          m("a[href='#tabs-1']", [
            glyph("th"), m("span.tab-legend", t("Viðureignir")),
            m("span",
              {
                id: 'numgames',
                style: numGames ? 'display: inline-block' : ''
              },
              numGames
            )
          ])
        ),
        m("li",
          m("a[href='#tabs-2']", [
            glyph("hand-right"), m("span.tab-legend", t("Áskoranir")),
            // Blink if we have timed games where the opponent is ready
            m("span" + (model.oppReady ? ".opp-ready" : ""),
              {
                id: "numchallenges",
                style: numChallenges ? 'display: inline-block' : ''
              },
              numChallenges
            )
          ])
        ),
        m("li",
          m("a[href='#tabs-3']",
            [glyph("user"), m("span.tab-legend", t("Andstæðingar"))]
          )
        ),
        m("li.no-mobile-list",
          m("a[href='#tabs-4']",
            [glyph("bookmark"), m("span.tab-legend", t("Ferill"))]
          )
        )
      ])
    ];
  }
};

const RecentListWithHeader: Component<IAttributes> = {
  // Recent games list with header
  view: (vnode) => {
    const { view } = vnode.attrs;
    const { model } = view;
    if (model.recentList === null)
      model.loadRecentList();
    return [
      m(".listitem.listheader",
        [
          m("span.list-win", glyphGrayed("bookmark", { title: ts('Sigur') })),
          mt("span.list-ts-short", "Viðureign lauk"),
          mt("span.list-nick", "Andstæðingur"),
          mt("span.list-scorehdr", "Úrslit"),
          m("span.list-elo-hdr",
            [
              m("span.glyphicon.glyphicon-user.elo-hdr-left", { title: ts('Mennskir andstæðingar') }),
              t("Elo"),
              m("span.glyphicon.glyphicon-cog.elo-hdr-right", { title: ts('Allir andstæðingar') })
            ]
          ),
          mt("span.list-duration", "Lengd"),
          m(ProModeIndicator, { header: true })
        ]
      ),
      m(RecentList, { view, id: "recentlist", recentList: model.recentList || [] })
    ];
  }
};

interface IUserButtonAttributes extends IAttributes {
  buttonId: string;
  icon: string;
  text: string;
}

const UserButton: Component<IUserButtonAttributes> = {
  // User list type button (robots, fav, alike, elo)
  view: (vnode) => {
    const { view, buttonId, icon, text } = vnode.attrs;
    const { model } = view;
    const sel = model.userListCriteria?.query || "robots";
    return m("span",
      {
        className: (buttonId === sel ? "shown" : ""),
        id: buttonId,
        onclick: (ev: Event) => {
          if (buttonId === "elo") {
            // Load Elo rating list, defaulting to the human-only rating
            model.userListCriteria = { query: "elo", spec: "human" };
            model.userList = null;
            view.actions.withSpinner(
              () => model.loadEloRatingList("human")
            );
          }
          else {
            // Load user list
            view.actions.loadUsersByType(buttonId, true);
            model.eloRatingSpec = null;
            model.eloRatingList = null;
          }
          ev.preventDefault();
        }
      },
      [glyph(icon, { style: { padding: 0 } }), nbsp(), text]
    );
  }
};

const OwnStats: Component<IAttributes> = {
  // User's own statistics summary
  view: (vnode) => {
    const { view } = vnode.attrs;
    const { model } = view;
    const { ownStats } = model;
    if (ownStats === null)
      model.loadOwnStats();
    return ownStats ? m(StatsDisplay, { view, id: 'own-stats', ownStats }) : null;
  }
};

const OwnBest: Component<IAttributes> = {
  // User's own best game and word scores
  view: (vnode) => {
    const { view } = vnode.attrs;
    const { model } = view;
    const { ownStats } = model;
    if (ownStats === null)
      model.loadOwnStats();
    return ownStats ? m(BestDisplay, { id: 'own-best', ownStats, myself: true }) : null;
  }
};

const Tab1: Component<IAttributes> = {
  // Ongoing games tab
  view: (vnode) => {
    const { view } = vnode.attrs;
    return m("div", { id: 'tabs-1' },
      [
        mt("p.no-mobile-block",
          [
            mt("strong", "Viðureignir sem standa yfir"),
            "click_on_game", // "Click on a game to view it and make a move if"
            glyph("flag"),
            " þú átt leik"
          ]
        ),
        m(GameList, { view, id: 'gamelist' })
      ]
    );
  }
};

const Tab2: Component<IAttributes> = {
  // Challenges received and sent tab
  view: (vnode) => {
    const { view } = vnode.attrs;
    return m("div", { id: 'tabs-2' },
      [
        mt("p.no-mobile-block",
          [
            mt("strong", "Skorað á þig"),
            "click_on_challenge", // "Click on a challenge to accept it and start a game, or on"
            glyph("thumbs-down", { style: { "margin-left": "6px", "margin-right": "6px" } }),
            " til að hafna henni"
          ]
        ),
        m(ChallengeList, { view, showReceived: true }),
        mt("p.no-mobile-block",
          [
            mt("strong", "Þú skorar á aðra"),
            " - smelltu á ",
            glyph("hand-right", { style: { "margin-left": "6px", "margin-right": "6px" } }),
            " til að afturkalla áskorun"
          ]
        ),
        m(ChallengeList, { view, showReceived: false })
      ]
    );
  }
};

const Tab3: Component<IAttributes> = {
  // Opponent lists tab
  view: (vnode) => {
    const { view } = vnode.attrs;
    return m("div", { id: 'tabs-3' },
      [
        m("div", { id: 'initials' },
          [
            m(".user-cat[id='user-headings']",
              [
                m(UserButton, { view, buttonId: "robots", icon: "cog", text: ts("Þjarkar") }),
                " ",
                m(UserButton, { view, buttonId: "fav", icon: "star", text: ts("Uppáhalds") }),
                " ",
                m(UserButton, { view, buttonId: "live", icon: "flash", text: ts("Álínis") }),
                " ",
                m(UserButton, { view, buttonId: "alike", icon: "resize-small", text: ts("Svipaðir") }),
                " ",
                m(UserButton, { view, buttonId: "elo", icon: "crown", text: ts("Topp 100") })
              ]
            ),
            m(SearchButton, { view })
          ]
        ),
        m(UserList, { view, id: 'userlist' })
      ]
    );
  }
};

const Tab4: Component<IAttributes> = {
  // Recent games and statistics tab
  view: (vnode) => {
    const { view } = vnode.attrs;
    return m("div", { id: 'tabs-4' },
      [
        m(OwnStats, { view }),
        m(OwnBest, { view }),
        mt("p.no-mobile-block",
          [
            mt("strong", "Nýlegar viðureignir þínar"),
            "click_to_review" // "Click on a game to review it"
          ]
        ),
        m(RecentListWithHeader, { view })
      ]
    );
  }
};

const MainTabs: Component<IAttributes> = {
  // Main tabs component containing all tabs and their content
  view: (vnode) => {
    const { view } = vnode.attrs;
    return m(".tabbed-page",
      m("div", { id: 'main-tabs' },
        [
          m(MainTabHeader, { view }),
          m("div.tab-scroll-area", [
            m(Tab1, { view }),
            m(Tab2, { view }),
            m(Tab4, { view }),
            m(Tab3, { view }),
          ]),
        ]
      )
    );
  }
};

export const Main: Component<IAttributes> = {
  // Main screen with tabs
  view: (vnode) => {
    const { view } = vnode.attrs;
    return [
      m(LeftLogo, { hidden: true }), // No legend, scale up by 50%
      m(UserId, { view }),
      m(Info),
      m(TogglerReady, { view }),
      m(TogglerReadyTimed, { view }),
      m("div",
        {
          oncreate: (vnode) => {
            makeTabs(view, "main-tabs", undefined, false, vnode);
          },
          onupdate: updateSelection
        },
        m(MainTabs, { view }),
      ),
    ];
  }
};
